<template>
  <div v-if="parcels.length || hasUnregistered" class="ov-card osh">
    <div class="ov-card__head osh__head">
      <v-icon small class="ov-card__icon">mdi-map-marker-path</v-icon>
      Sendungsverlauf
      <span v-if="summary" class="osh__summary" :style="{ color: statusMeta(summary.status).color }">
        <v-icon x-small :color="statusMeta(summary.status).color">{{ statusMeta(summary.status).icon }}</v-icon>
        {{ statusMeta(summary.status).label }}
        <span v-if="summary.flag" class="osh__flag" :style="{ background: flagMeta(summary.flag).color }">{{ flagMeta(summary.flag).label }}</span>
      </span>
      <v-spacer />
      <v-btn v-if="hasUnregistered" x-small depressed color="primary" :loading="busy === 'register'" :disabled="!!busy" @click="run('register')">
        <v-icon x-small left>mdi-cloud-upload-outline</v-icon>Bei 17TRACK anmelden
      </v-btn>
      <v-btn v-if="parcels.length" x-small text :loading="busy === 'refresh'" :disabled="!!busy" @click="run('refresh')">
        <v-icon x-small left>mdi-refresh</v-icon>Aktualisieren
      </v-btn>
    </div>
    <div v-if="message" class="osh__msg" :class="{ 'osh__msg--err': messageError }">{{ message }}</div>

    <div v-for="p in parcels" :key="p.code" class="osh__parcel">
      <div class="osh__parcelHead">
        <span class="osh__status" :style="{ color: statusMeta(p.status).color }">
          <v-icon small :color="statusMeta(p.status).color">{{ statusMeta(p.status).icon }}</v-icon>
          {{ statusMeta(p.status).label }}
        </span>
        <span v-if="p.flag" class="osh__flag" :style="{ background: flagMeta(p.flag).color }">{{ flagMeta(p.flag).label }}</span>
        <span class="osh__meta">
          {{ p.fulfiller }} · {{ p.carrier }} ·
          <a v-if="p.trackingUrl" :href="p.trackingUrl" target="_blank">{{ p.code }}</a><span v-else>{{ p.code }}</span>
          <span v-if="p.items > 1"> · {{ p.items }} Stk.</span>
        </span>
        <span v-if="p.bizDays !== null" class="osh__meta" :title="p.status === 'delivered' ? 'Werktage Mo–Fr von Versand bis Zustellung' : 'Werktage Mo–Fr seit Versand'">· {{ p.bizDays }} Werktage {{ p.status === 'delivered' ? 'Laufzeit' : 'unterwegs' }}</span>
        <span v-if="p.error" class="osh__err" :title="p.error">Fehler: {{ p.error }}</span>
        <span v-if="!p.registeredAt" class="osh__meta osh__meta--warn">nicht registriert</span>
      </div>
      <ul v-if="p.events.length" class="osh__events">
        <li v-for="(e, i) in p.events" :key="i" class="osh__event" :class="{ 'osh__event--latest': i === 0 }">
          <span class="osh__when" :title="e.at ? ago(e.at) : ''">{{ e.at ? dateOf(e.at) : "" }}</span>
          <span class="osh__text">{{ e.text }}</span>
          <span v-if="e.location" class="osh__loc">{{ e.location }}</span>
        </li>
      </ul>
      <div v-else class="osh__meta osh__meta--empty">
        {{ p.registeredAt ? "Noch keine Ereignisse vom Carrier" : "Noch nicht bei 17TRACK angemeldet" }}
        <span v-if="p.shippingDate"> · versandt {{ ago(p.shippingDate) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// Sendungsverlauf einer Bestellung (17TRACK, orders.shipments / orders.shipmentSummary):
// je Paket Status + Flag + Ereignis-Liste (neueste zuerst). Knöpfe rufen shipmentAction
// (register = anmelden + sofort Stand holen, refresh = Stand holen).
import { httpsCallable } from "firebase/functions";
import { functions as fbFunctions } from "../../../../fb";
import { getDateFromNow } from "@/utils";
import { bizDaysBetween, shipmentFlagMeta, shipmentStatusMeta } from "@/utils/shipments";
import dayjs from "dayjs";

const WEEKDAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

export default {
  name: "OrderShipments",
  props: { order: { type: Object, required: true } },
  data() {
    return { busy: "", message: "", messageError: false };
  },
  computed: {
    summary() {
      return this.order.shipmentSummary || null;
    },
    /** Tracking-Codes aus den Fulfillments (auch ohne 17TRACK-Eintrag) + gespeicherter Stand */
    parcels() {
      const known = this.order.shipments || {};
      const out = {};
      for (const [fulfiller, list] of Object.entries(this.order.fulfillment || {})) {
        for (const f of Array.isArray(list) ? list : [list]) {
          for (const item of (f && f.fulfillItems) || []) {
            const sh = item && item.shipment;
            const code = sh && String(sh.trackingCode || "").trim();
            if (!code || code === "Not Given" || code.includes("@")) continue;
            if (!out[code]) {
              const k = known[code] || {};
              out[code] = {
                code,
                fulfiller,
                carrier: k.carrier || sh.carrier || "",
                trackingUrl: k.trackingUrl || sh.trackingUrl || null,
                shippingDate: k.shippingDate || sh.shippingDate || null,
                status: k.status || "registered",
                flag: k.flag || null,
                registeredAt: k.registeredAt || null,
                deliveredAt: k.deliveredAt || null,
                // Werktage ab Versand bis Zustellung bzw. bis jetzt (statt 17TRACK-Kalendertagen, Malte 14.10.)
                bizDays: bizDaysBetween(k.shippingDate || sh.shippingDate, k.deliveredAt || (k.status && k.status !== "registered" && k.status !== "pending" ? Date.now() : null)),
                error: k.error || null,
                items: 0,
                events: Array.isArray(k.events) ? [...k.events].sort((a, b) => (b.at || 0) - (a.at || 0)) : [],
              };
            }
            out[code].items += Number(item.quantity) || 1;
          }
        }
      }
      return Object.values(out);
    },
    hasUnregistered() {
      return this.parcels.some((p) => !p.registeredAt);
    },
  },
  methods: {
    statusMeta: shipmentStatusMeta,
    flagMeta: shipmentFlagMeta,
    ago(v) {
      return getDateFromNow(typeof v === "number" ? new Date(v) : v);
    },
    /** „Mo 14.10. 08:32" — deutscher Wochentag ohne globale dayjs-Locale (würde sonst alle Relativzeiten umstellen) */
    dateOf(v) {
      const d = dayjs(v);
      return `${WEEKDAYS_DE[d.day()]} ${d.format("DD.MM. HH:mm")}`;
    },
    async run(action) {
      this.busy = action;
      this.message = "";
      this.messageError = false;
      try {
        const fn = httpsCallable(fbFunctions, "shipmentAction", { timeout: 120000 });
        const res = await fn({ action, orderUid: this.order.uid });
        const d = (res && res.data) || {};
        if (!d.success) throw new Error(d.error || "Fehlgeschlagen");
        this.message = action === "register"
          ? `Angemeldet: ${d.registered || 0} · aktualisiert: ${d.updated || 0}${d.rejected ? ` · ohne Daten: ${d.rejected}` : ""}`
          : `Aktualisiert: ${d.updated || 0}${d.rejected ? ` · ohne Daten: ${d.rejected}` : ""}`;
      } catch (e) {
        this.message = e.message || String(e);
        this.messageError = true;
      } finally {
        this.busy = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.osh {
  &__head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  &__summary { font-size: 12px; font-weight: 600; display: inline-flex; align-items: center; gap: 4px; margin-left: 6px; }
  &__flag { display: inline-block; margin-left: 6px; color: #fff; font-size: 10px; font-weight: 700; border-radius: 4px; padding: 1px 6px; text-transform: uppercase; }
  &__msg { font-size: 12px; margin: 4px 0 8px; color: #2e7d32; &--err { color: #d8493f; } }
  &__parcel { padding: 8px 0 10px; border-top: 1px solid rgba(0, 0, 0, 0.06); &:first-of-type { border-top: 0; } }
  &__parcelHead { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 12px; }
  &__status { font-weight: 700; display: inline-flex; align-items: center; gap: 4px; }
  &__meta { color: rgba(0, 0, 0, 0.55); &--warn { color: #ef6c00; font-weight: 600; } &--empty { margin-top: 4px; font-size: 12px; } }
  &__err { color: #d8493f; font-size: 11px; max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__events { list-style: none; padding: 6px 0 0 4px; margin: 0; }
  &__event {
    display: grid; grid-template-columns: 112px 1fr auto; gap: 10px; font-size: 12px; padding: 3px 0; color: rgba(0, 0, 0, 0.6);
    &--latest { color: rgba(0, 0, 0, 0.9); font-weight: 600; }
  }
  &__when { white-space: nowrap; }
  &__loc { color: rgba(0, 0, 0, 0.45); white-space: nowrap; }
}
</style>
